// @flow

import React from "react";

import { Stack, Card, Typography, Box, CircularProgress, Snackbar, Alert, Button } from "@mui/material";

import moment from "moment";

import axios from 'axios';

import { TENANT } from "../../constants";

import { useAppContext } from "../../context/AppContext";

import { useLanguage } from "../../i18n";

const formatDateTime = (m) => {
    return m
        .utc()
        .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .format("YYYY-MM-DD[T]HH:mm:ss.SSSZ");
}

const PickupDateSelector = ({ inProgress, disabled, value, onChange, t }) => {
    const dates = React.useMemo(() => {
        return [0, 1, 2].map((offset) => {
            const date = moment().add(offset, "days");

            return {
                date: date,
                label:
                    offset === 0
                        ? t('today')
                        : offset === 1
                          ? t('tomorrow')
                          : date.format("ddd, MMM D"),
            };
        });
    }, [t]);

    return (
        <Stack spacing={1}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                {dates.map(({ date, label }) => {
                    const selected = value === formatDateTime(date);
                    
                    return (
                        <Box
                            key={label}
                            sx={{
                                position: "relative",
                                flex: { xs: "1 1 calc(50% - 8px)", sm: "0 0 auto" },
                                minWidth: { xs: "auto", sm: 110 },
                            }}
                        >
                            <Card
                                onClick={
                                    disabled || inProgress ? undefined : () => onChange(formatDateTime(date))
                                }
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    border: "2px solid",
                                    borderColor: selected
                                        ? "primary.main"
                                        : "divider",
                                    bgcolor: selected
                                        ? "primary.light"
                                        : "background.paper",
                                    transition: "transform 0.1s ease",
                                    "&:active": {
                                        transform: "scale(0.97)",
                                    },
                                }}
                            >
                                <Typography fontWeight={600}>{label}</Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {date.format("MMM D")}
                                </Typography>
                            </Card>

                            {inProgress && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Stack>
    );
};

type Props = {
    stepNo: number,
    activeStep: number,
    handleNext: () => void,
};

const PickupDateStep = (props: Props): any => {
    const { state, setState } = useAppContext();
    const { t } = useLanguage();

    const [inProgress, setInProgress] = React.useState(false);

    const [snackbarMessage, setSnackbarMessage] = React.useState(null);
    const handleShowError = (error: string) => {
        setSnackbarMessage(error);
    };
    const handleSnackbarClose = (_, reason) => {
        // Prevent closing on clickaway (optional)
        if (reason === "clickaway") return;
        setSnackbarMessage(null);
    };

    const setPickupDate = async (pickupDate: any) => {
        setInProgress(true);
        const url = state.returnRequest._links.changePickupDate.href;
        try {
            const response = await axios.post(
                url,
                {
                    pickupDate
                },
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    }
                },
            );
            
            setState({ ...state, returnRequest: response.data });

            // move next
            props.handleNext();
        } catch (error) {
            console.error("Error update picking address:", error);

            const errorData = error.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes
                    .map((cause) => cause.message)
                    .join("; ");
                handleShowError(messages);
            } else {
                handleShowError(
                    errorData?.message || t('errorOccurred'),
                );
            }
        } finally {
            setInProgress(false);
        }
    }

    const pickupDate = state.returnRequest.pickupDate;

    const isNew = state.returnRequest?.state === 'NEW';

    return (
        <>
            <Snackbar
                open={snackbarMessage != null}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Stack spacing={2}>
                <Typography variant="caption">{t('selectPickupDate')}</Typography>

                <PickupDateSelector 
                    inProgress={inProgress}
                    value={pickupDate}
                    disabled={!isNew}
                    t={t}
                    onChange={(pickupDate) => {
                        setPickupDate(pickupDate);
                        props.handleNext();
                    }} 
                />

                {props.activeStep === props.stepNo && pickupDate != null ? (
                    <Button
                        variant="contained"
                        onClick={props.handleNext}
                        sx={{ mt: 1, mr: 1 }}
                    >
                        {t('continue')}
                    </Button>
                ) : null}
            </Stack>
        </>
    );
};

export default PickupDateStep;
